/**
 * Email validation utilities for bulk upload processing.
 * Validates email format before sending to API to prevent blocking errors.
 */

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates email format for bulk upload processing.
 * Checks for common issues that would cause API failures.
 */
export function validateEmailFormat(email: string): EmailValidationResult {
  if (!email) {
    return { isValid: true }; // Empty emails handled elsewhere
  }

  // Check for whitespace (leading/trailing/internal)
  const trimmed = email.trim();
  if (email !== trimmed || email.includes(' ')) {
    return { isValid: false, error: 'contains whitespace' };
  }

  // Check for exactly one @ symbol
  const atCount = (email.match(/@/g) || []).length;
  if (atCount === 0) {
    return { isValid: false, error: 'missing @ symbol' };
  }
  if (atCount > 1) {
    return { isValid: false, error: 'contains multiple @ symbols' };
  }

  // Basic format check: something@something.something
  const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicEmailRegex.test(email)) {
    return { isValid: false, error: 'invalid format' };
  }

  return { isValid: true };
}

/**
 * Checks if email is valid for API submission.
 * Use this to filter emails before sending to bulk validation endpoint.
 */
export function isEmailValidForApi(email: string): boolean {
  return validateEmailFormat(email).isValid;
}
